import { useParams, useRouteContext } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import { Container } from "react-bootstrap";
import { UseFormReturn, useWatch } from "react-hook-form";

import DateInput from "@/components/shared/forms/DateInput";
import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import RadioFieldRow from "@/components/shared/forms/RadioFieldRow";
import RadioSelect from "@/components/shared/forms/RadioSelect";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import { type LANG, LANG_TEMPLATE } from "@/models.ts";
import { useCoreData } from "@/queries.ts";
import { FaBriefcase } from "react-icons/fa";
import { MdLanguage } from "react-icons/md";

import { useLANGFormLogic } from "./useLANGFormLogic";
import { LanguageMultiSelect } from "@/components/shared/LanguageMultiSelect";

type LanguageOption = {
  Text: string;
  Value: string;
};

export function LANGAddEditForm({
  selectedLang,
  form,
  langDefaults,
}: {
  selectedLang: LANG | undefined;
  form: UseFormReturn<LANG, any, LANG>;
  langDefaults?: Partial<LANG>;
}) {
  const params = useParams({
    shouldThrow: false,
    from: "/$leaid/_error-wrapper/records/child/$entityid",
  });

  const newLangTemplate: LANG = {
    ...LANG_TEMPLATE,
    ...langDefaults,
    CSPPID: langDefaults?.CSPPID ?? params?.entityid ?? "",
  };

  const lang = selectedLang ?? newLangTemplate;

  const resetKey =
    (selectedLang as any)?.RecordId ??
    `${(lang as any).CSPPID ?? ""}|${(lang as any).EffectiveStartDate ?? ""}|${
      (lang as any).ProgramYear ?? ""
    }|${(lang as any).PreschoolCode ?? ""}|${(lang as any).LocalChildId ?? ""}`;

  const leaContext = useRouteContext({ from: "/$leaid" });
  const viewOnly = !leaContext.hasRole("LANGEdit");
  const coreData = useCoreData();
  const tooltipMap = leaContext.coreData.tooltipMap.get("LANG");

  const {
    register,
    control,
    setValue,
    clearErrors,
    trigger,
    getValues,
    formState: { errors },
  } = form;

  const programYear = useWatch({ control, name: "ProgramYear" });

  const instrumentOptOut = useWatch({
    control,
    name: "InstrumentOptOut" as any,
  }) as string | undefined;

  const dllInstrumentDesignated = useWatch({
    control,
    name: "DLLInstrumentDesignated" as any,
  }) as string | undefined;

  const dllTeacherDesignated = useWatch({
    control,
    name: "DLLTeacherDesignated" as any,
  }) as string | undefined;

  const instrumentDate = useWatch({
    control,
    name: "InstrumentDate" as any,
  }) as string | undefined;

  const dllTeacherDesignatedDate = useWatch({
    control,
    name: "DLLTeacherDesignatedDate" as any,
  }) as string | undefined;

  const prevDllInstrumentDesignatedRef = useRef<string | undefined>(undefined);

  const languagesOptions = useMemo(() => {
    return (coreData.languagesList ?? []) as LanguageOption[];
  }, [coreData.languagesList]);

  const langLogic = useLANGFormLogic({
    form,
    lang,
    languagesOptions,
    resetKey,
    instrumentOptOut,
  });

  useEffect(() => {
    register("InstrumentLanguagesHeard1" as any, {
      validate: (v: any) => {
        if (instrumentOptOut === "Y") {
          return v == null || String(v).trim().length === 0
            ? true
            : 'Instrument Languages Heard must be blank when Instrument Opt Out = "Y"';
        }

        if (instrumentOptOut === "N") {
          const heard = v;
          const spoken = getValues("InstrumentLanguagesSpoken1" as any) as
            | string
            | undefined;
          const understood = getValues(
            "InstrumentLanguagesUnderstood1" as any
          ) as string | undefined;

          const hasAny =
            (heard && String(heard).trim().length > 0) ||
            (spoken && String(spoken).trim().length > 0) ||
            (understood && String(understood).trim().length > 0);

          if (!hasAny) {
            return "Instrument Opt Out must be 'Y' when no Instrument Language Heard, Spoken, or Understood are filled out";
          }
        }

        return true;
      },
    });
  }, [register, instrumentOptOut, getValues]);

  useEffect(() => {
    register("InstrumentLanguagesUnderstood1" as any, {
      validate: (v: any) => {
        if (instrumentOptOut === "Y") {
          return v == null || String(v).trim().length === 0
            ? true
            : 'Instrument Languages Understood must be blank when Instrument Opt Out = "Y"';
        }
        return true;
      },
    });
  }, [register, instrumentOptOut]);

  useEffect(() => {
    register("InstrumentLanguagesSpoken1" as any, {
      validate: (v: any) => {
        if (instrumentOptOut === "Y") {
          return v == null || String(v).trim().length === 0
            ? true
            : 'Instrument Languages Spoken must be blank when Instrument Opt Out = "Y"';
        }
        return true;
      },
    });
  }, [register, instrumentOptOut]);

  useEffect(() => {
    if (viewOnly) return;

    const prev = prevDllInstrumentDesignatedRef.current;
    prevDllInstrumentDesignatedRef.current = dllInstrumentDesignated;

    if (dllInstrumentDesignated === "Y") {
      setValue("DLLTeacherDesignated" as any, "N", {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    if (dllInstrumentDesignated === "N" && prev === "Y") {
      setValue("DLLTeacherDesignated" as any, undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
      clearErrors("DLLTeacherDesignated" as any);
    }
  }, [dllInstrumentDesignated, viewOnly, setValue, clearErrors]);

  useEffect(() => {
    if (viewOnly) return;
    if (instrumentOptOut === "Y") {
      setValue("InstrumentDate" as any, "", { shouldDirty: true });
      clearErrors("InstrumentDate" as any);
      clearErrors("InstrumentLanguagesHeard1" as any);
      clearErrors("InstrumentLanguagesUnderstood1" as any);
      clearErrors("InstrumentLanguagesSpoken1" as any);
    }
  }, [instrumentOptOut, viewOnly, setValue, clearErrors]);

  useEffect(() => {
    if (viewOnly) return;
    if (dllTeacherDesignated === "N") {
      setValue("DLLTeacherDesignatedDate" as any, "", { shouldDirty: true });
      clearErrors("DLLTeacherDesignatedDate" as any);
    }
  }, [dllTeacherDesignated, viewOnly, setValue, clearErrors]);

  useEffect(() => {
    if (instrumentDate && dllTeacherDesignatedDate) {
      trigger("DLLTeacherDesignatedDate" as any);
    }
  }, [instrumentDate, dllTeacherDesignatedDate, trigger]);

  const isHeardDisabled = viewOnly || instrumentOptOut === "Y";
  const isUnderstoodDisabled = viewOnly || instrumentOptOut === "Y";
  const isSpokenDisabled = viewOnly || instrumentOptOut === "Y";
  const isInstrumentDateDisabled = viewOnly || instrumentOptOut === "Y";
  const isDllTeacherDesignatedDisabled =
    viewOnly || dllInstrumentDesignated === "Y";
  const isDllTeacherDesignatedDateDisabled =
    viewOnly || dllTeacherDesignated === "N";

  const preschoolOptions = leaContext
    .currentOrgPreschoolsByProgramYear(programYear)
    .map((x) => ({
      Text: x.PreschoolName,
      Value: x.PreschoolCDSCode,
    }));

  return (
    <Container
      className="ps-3 pe-3 overview-container"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FaBriefcase className="section-icon" />
          <h5 className="section-title">Agency Information</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="CSPPID"
              labelText="CSPPID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!!(lang as any).CSPPID}
                  maxLength={50}
                  {...register(name)}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="EffectiveStartDate"
              labelText="Effective Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  max="9999-12-31"
                  disabled={viewOnly}
                  {...register(name, {
                    validate: langLogic.validateEffectiveStartDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                !!selectedLang?.ProgramYear && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Program Year is required" }}
                    options={coreData.programYearList}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(name, {
                      required: "Program Year is required",
                    })}
                    options={coreData.programYearList}
                  />
                )
              }
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="PreschoolCode"
              labelText="Preschool"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                !!selectedLang?.PreschoolCode && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Preschool is required" }}
                    options={preschoolOptions}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(name, { required: "Preschool is required" })}
                    options={preschoolOptions}
                  />
                )
              }
            </FormFieldRow>
          </div>

          <div className="col-md-6">
            <FormFieldRow
              name="LocalChildId"
              labelText="Local Child ID (CICN)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!!(lang as any).LocalChildId}
                  maxLength={50}
                  {...register(name, {
                    required: "Local Child ID (CICN) is required",
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>

      <div className="form-card-modern mb-3">
        <div className="section-header">
          <MdLanguage className="section-icon" />
          <h5 className="section-title">Language Information</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <RadioFieldRow
              name="InstrumentOptOut"
              labelText="Instrument Opt Out"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <RadioSelect
                  id={id}
                  disabled={viewOnly}
                  {...register(id, {
                    required: "Instrument Opt Out is required",
                  })}
                  options={[
                    { Text: "Yes", Value: "Y" },
                    { Text: "No", Value: "N" },
                  ]}
                />
              )}
            </RadioFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="InstrumentDate"
              labelText="Instrument Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={instrumentOptOut === "N"}
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  disabled={isInstrumentDateDisabled}
                  max="9999-12-31"
                  {...register(name, {
                    validate: langLogic.validateInstrumentDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <br />

        <h6>Interview Languages</h6>
        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="InstrumentLanguagesHeard1"
              labelText="Language(s) Heard"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name, validationProps) => (
                <div
                  className={isHeardDisabled ? "kendo-ms-disabled" : undefined}
                >
                  <LanguageMultiSelect
                    id={id}
                    ariaLabel="Language(s) Heard"
                    ariaDescribedBy={validationProps["aria-describedby"]}
                    disabled={isHeardDisabled}
                    invalid={!!validationProps["aria-invalid"]}
                    data={languagesOptions}
                    value={langLogic.heard.value}
                    onChange={(items) => {
                      langLogic.heard.onChange(items);
                      trigger("InstrumentLanguagesHeard1" as any);
                    }}
                  />
                </div>
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="InstrumentLanguagesUnderstood1"
              labelText="Language(s) Understood"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name, validationProps) => (
                <div
                  className={
                    isUnderstoodDisabled ? "kendo-ms-disabled" : undefined
                  }
                >
                  <LanguageMultiSelect
                    id={id}
                    ariaLabel="Language(s) Understood"
                    ariaDescribedBy={validationProps["aria-describedby"]}
                    disabled={isUnderstoodDisabled}
                    invalid={!!validationProps["aria-invalid"]}
                    data={languagesOptions}
                    value={langLogic.understood.value}
                    onChange={(items) => {
                      langLogic.understood.onChange(items);
                      trigger([
                        "InstrumentLanguagesUnderstood1",
                        "InstrumentLanguagesHeard1",
                      ] as any);
                    }}
                  />
                </div>
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="InstrumentLanguagesSpoken1"
              labelText="Language(s) Spoken"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name, validationProps) => (
                <div
                  className={isSpokenDisabled ? "kendo-ms-disabled" : undefined}
                >
                  <LanguageMultiSelect
                    id={id}
                    ariaLabel="Language(s) Spoken"
                    ariaDescribedBy={validationProps["aria-describedby"]}
                    disabled={isSpokenDisabled}
                    invalid={!!validationProps["aria-invalid"]}
                    data={languagesOptions}
                    value={langLogic.spoken.value}
                    onChange={(items) => {
                      langLogic.spoken.onChange(items);
                      trigger([
                        "InstrumentLanguagesSpoken1",
                        "InstrumentLanguagesHeard1",
                      ] as any);
                    }}
                  />
                </div>
              )}
            </FormFieldRow>
          </div>
        </div>

        <br />

        <div className="row g-3">
          <div className="col-md-4">
            <RadioFieldRow
              name="DLLInstrumentDesignated"
              labelText="DLL Instrument Designated"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={instrumentOptOut === "Y"}
            >
              {(id) => (
                <RadioSelect
                  id={id}
                  disabled={viewOnly}
                  {...register(id, {
                    validate: (v: any) => {
                      if (instrumentOptOut === "Y") {
                        return v === "Y" || v === "N"
                          ? true
                          : "DLL Instrument Designated is required";
                      }
                      return true;
                    },
                  })}
                  options={[
                    { Text: "Yes", Value: "Y" },
                    { Text: "No", Value: "N" },
                  ]}
                />
              )}
            </RadioFieldRow>
          </div>

          <div className="col-md-4">
            <RadioFieldRow
              name="DLLTeacherDesignated"
              labelText="DLL Teacher Designated"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <RadioSelect
                  id={id}
                  disabled={isDllTeacherDesignatedDisabled}
                  {...register(id, {
                    validate: (v: any) => {
                      if (dllInstrumentDesignated === "Y") {
                        return v === "N"
                          ? true
                          : 'DLL Teacher Designated must be "N" when DLL Instrument Designated = "Y"';
                      }
                      return true;
                    },
                  })}
                  options={[
                    { Text: "Yes", Value: "Y" },
                    { Text: "No", Value: "N" },
                  ]}
                />
              )}
            </RadioFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="DLLTeacherDesignatedDate"
              labelText="DLL Teacher Designated Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={dllTeacherDesignated === "Y"}
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  disabled={isDllTeacherDesignatedDateDisabled}
                  max="9999-12-31"
                  {...register(name, {
                    validate: (v: any) =>
                      langLogic.validateDLLTeacherDesignatedDate(
                        v,
                        dllTeacherDesignated
                      ),
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>
    </Container>
  );
}
